import React, { useState, useMemo, useEffect } from "react";
import { formatDateShort } from "../utils/format";

function paginate(array, pageSize, pageNumber) {
  // Returns a slice of array for the given page (1-based pageNumber)
  return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
}

const FleetBookingsOrganized = ({
  bookings,
  assignments,
  vans,
  openAssignModal,
  unassignBooking,
  deleteBooking,
  isLoading,
  isUnassigning,
  pageSize = 15
}) => {
  // Debug component state
  console.log('🔄 FleetBookingsOrganized render:', {
    bookingsCount: bookings?.length || 0,
    deleteBookingType: typeof deleteBooking,
    isLoading,
    timestamp: new Date().toISOString()
  });

  // Debug when deleteBooking prop changes
  useEffect(() => {
    console.log('🔄 deleteBooking prop changed:', {
      type: typeof deleteBooking,
      isFunction: typeof deleteBooking === 'function',
      timestamp: new Date().toISOString()
    });
  }, [deleteBooking]);

  const [activeTab, setActiveTab] = useState('upcoming');
  const [currentPage, setCurrentPage] = useState(1);

  // Get current time for filtering
  const now = useMemo(() => new Date(), []);

  // Sort bookings by datetime
  const sortedBookings = useMemo(() => {
    return [...bookings].sort((a, b) => new Date(a.datetime) - new Date(b.datetime));
  }, [bookings]);

  // Split bookings into upcoming and past
  const upcoming = useMemo(() => {
    return sortedBookings.filter(b => {
      const bookingDate = new Date(b.datetime);
      return bookingDate >= now;
    });
  }, [sortedBookings, now]);
  
  const past = useMemo(() => {
    return sortedBookings.filter(b => {
      const bookingDate = new Date(b.datetime);
      return bookingDate < now;
    });
  }, [sortedBookings, now]);

  // Get filtered bookings based on active tab
  const filteredBookings = useMemo(() => {
    return activeTab === 'upcoming' ? upcoming : past;
  }, [activeTab, upcoming, past]);

  const totalPages = Math.ceil(filteredBookings.length / pageSize);
  const currentBookings = paginate(filteredBookings, pageSize, currentPage);

  // Reset to page 1 when switching tabs
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const renderBooking = booking => {
    // Simple null check for assignments
    const bookingAssignments = assignments && assignments[booking.id] ? assignments[booking.id] : [];
    const hasAssignments = bookingAssignments.length > 0;
    
    // Extract van info
    let assignedVans = [];
    
    if (hasAssignments) {
      // Map vans
      assignedVans = bookingAssignments
        .map(assignment => {
          const van = vans.find(v => v.id === assignment.van_id);
          return van ? van.name : 'Unknown van';
        });
    }
    
    // Determine status classes
    let statusClass, statusBadgeClass;
    
    if (booking.status === 'confirmed') {
      statusClass = 'bg-green-900/20 border-green-800/50 hover:bg-green-900/30';
      statusBadgeClass = 'bg-green-800/80 text-green-300';
    } else if (booking.status === 'cancelled') {
      statusClass = 'bg-red-900/20 border-red-800/50 hover:bg-red-900/30';
      statusBadgeClass = 'bg-red-800/80 text-red-300';
    } else if (booking.status === 'completed') {
      statusClass = 'bg-blue-900/20 border-blue-800/50 hover:bg-blue-900/30';
      statusBadgeClass = 'bg-blue-800/80 text-blue-300';
    } else {
      // pending or other statuses
      statusClass = 'bg-white/20 border-white/50 hover:bg-white/30';
      statusBadgeClass = 'bg-white/80 text-white';
    }
    
    // Calculate capacity needs
    const neededVans = booking.vans_needed || Math.ceil(booking.passengers / 7);
    const assignedVansCount = assignedVans.length;
    
    // Determine assignment status
    let assignmentStatus = '';
    let assignmentStatusClass = '';
    
    if (hasAssignments) {
      if (assignedVansCount < neededVans) {
        assignmentStatus = 'Partially Assigned';
        assignmentStatusClass = 'bg-white/50 text-white';
      } else {
        assignmentStatus = 'Fully Assigned';
        assignmentStatusClass = 'bg-green-800/50 text-green-300';
      }
    } else {
      assignmentStatus = 'Unassigned';
      assignmentStatusClass = 'bg-gray-800/60 text-gray-400';
    }
    
    return (
      <div 
        key={booking.id}
        className={`p-5 rounded-lg border transition-colors ${statusClass}`}
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left column - booking info */}
          <div className="flex-1">
            <div className="flex justify-between mb-3">
              <h3 className="text-lg font-bold text-white">{booking.full_name}</h3>
              <div className="flex gap-2">
                <span className={`px-2 py-1 text-xs rounded-md uppercase font-medium ${statusBadgeClass}`}>
                  {booking.status}
                </span>
                <span className={`px-2 py-1 text-xs rounded-md font-medium ${assignmentStatusClass}`}>
                  {assignmentStatus}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 flex-shrink-0 text-white">🗓️</span>
                <div>
                  <div className="text-gray-400">Pickup time</div>
                  <div className="text-white font-medium">{formatDateShort(booking.datetime)}</div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 flex-shrink-0 text-white">👥</span>
                <div>
                  <div className="text-gray-400">Passengers & Luggage</div>
                  <div className="text-white">
                    {booking.passengers} passengers, {booking.luggage} {booking.luggage === 1 ? 'item' : 'items'}
                    <div className="text-xs text-white/80 mt-0.5">
                      {neededVans} {neededVans === 1 ? 'van' : 'vans'} required
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 flex-shrink-0 text-white">📍</span>
                <div>
                  <div className="text-gray-400">Pickup</div>
                  <div className="text-white">{booking.pickup_name}</div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 flex-shrink-0 text-white">🏁</span>
                <div>
                  <div className="text-gray-400">Dropoff</div>
                  <div className="text-white">{booking.dropoff_name}</div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 flex-shrink-0 text-white">📧</span>
                <div>
                  <div className="text-gray-400">Contact</div>
                  <div className="text-white text-xs">
                    <div>{booking.email}</div>
                    <div className="text-gray-400">{booking.phone}</div>
                  </div>
                </div>
              </div>
              <div className="flex items-start">
                <span className="mr-2 mt-0.5 flex-shrink-0 text-white">💰</span>
                <div>
                  <div className="text-gray-400">Price</div>
                  <div className="text-white font-semibold">€{booking.price}</div>
                  {booking.distance && (
                    <div className="text-xs text-gray-400">{booking.distance}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* Right column - assignments and actions */}
          <div className="lg:w-64 flex flex-col">
            {hasAssignments && (
              <div className="bg-black/20 rounded-lg p-3 mb-3 flex-1">
                <h4 className="text-xs uppercase text-gray-400 mb-2">Assigned Resources</h4>
                <div className="space-y-2">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Vans ({assignedVans.length}/{neededVans})</div>
                    <div className="flex flex-wrap gap-1.5">
                      {assignedVans.map((vanName, idx) => (
                        <span key={`van-${idx}`} className="px-2 py-1 text-xs bg-gray-800 rounded-sm flex items-center">
                          🚐 {vanName}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2 mt-auto">
              {/* Only show assignment actions for active bookings (not cancelled/completed) */}
              {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                <>
                  {hasAssignments && (
                    <button
                      onClick={() => unassignBooking(booking.id)}
                      className="px-4 py-2 bg-red-900/70 text-red-200 text-sm rounded-md flex items-center justify-center hover:bg-red-800/70 transition-colors"
                      disabled={isUnassigning}
                    >
                      ❌ Unassign
                    </button>
                  )}
                  {hasAssignments && (
                    <button
                      onClick={() => openAssignModal(booking)}
                      className="px-4 py-2 bg-blue-800/70 text-blue-100 text-sm rounded-md flex items-center justify-center hover:bg-blue-700/70 transition-colors"
                      disabled={isLoading}
                    >
                      ✏️ Edit Assignment
                    </button>
                  )}
                  {!hasAssignments && (
                    <button
                      onClick={() => openAssignModal(booking)}
                      className="px-4 py-2 bg-white text-black text-sm rounded-md flex items-center justify-center hover:bg-white transition-colors"
                      disabled={isLoading}
                    >
                      🚐 Assign Vans
                    </button>
                  )}
                  {hasAssignments && assignedVans.length < neededVans && (
                    <button
                      onClick={() => openAssignModal(booking)}
                      className="px-4 py-2 bg-white/70 text-white text-sm rounded-md flex items-center justify-center hover:bg-white/70 transition-colors"
                      disabled={isLoading}
                    >
                      🚐 Add More Vans
                    </button>
                  )}
                </>
              )}
              {/* Show status message for inactive bookings */}
              {(booking.status === 'cancelled' || booking.status === 'completed') && (
                <div className="px-4 py-2 bg-gray-800/50 text-gray-400 text-sm rounded-md text-center">
                  {booking.status === 'cancelled' ? '❌ Booking Cancelled' : '✅ Booking Completed'}
                </div>
              )}
              {/* Delete Booking Button - Always show as last action */}
              <button
                onClick={() => {
                  // Simple direct call to avoid any closure issues
                  if (typeof deleteBooking === 'function') {
                    deleteBooking(booking.id, booking.full_name);
                  } else {
                    console.error('Delete function not available');
                    alert('Delete function not available. Please refresh the page.');
                  }
                }}
                className="px-4 py-2 bg-red-800/70 text-red-200 text-sm rounded-md flex items-center justify-center hover:bg-red-700/70 transition-colors border border-red-700/50 mt-2"
                disabled={isLoading}
                title="Delete this booking permanently"
              >
                🗑️ Delete Booking
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Summary Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-4">
          <div className="text-green-400 text-sm font-medium">Upcoming Bookings</div>
          <div className="text-2xl font-bold text-white mt-1">{upcoming.length}</div>
        </div>
        <div className="bg-white/20 border border-white/50 rounded-lg p-4">
          <div className="text-white text-sm font-medium">Past Bookings</div>
          <div className="text-2xl font-bold text-white mt-1">{past.length}</div>
        </div>
        <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
          <div className="text-blue-400 text-sm font-medium">Total Active</div>
          <div className="text-2xl font-bold text-white mt-1">{bookings.length}</div>
        </div>
      </div>
      
      {/* Tabs for Upcoming/Past */}
      <div className="flex space-x-4 mb-6">
        <button
          className={`px-5 py-2 rounded-t-lg font-semibold border-b-2 transition-colors duration-150 ${
            activeTab === 'upcoming' 
              ? 'border-white text-white bg-black' 
              : 'border-transparent text-gray-400 bg-transparent hover:text-white'
          }`}
          onClick={() => handleTabChange('upcoming')}
        >
          Upcoming
        </button>
        <button
          className={`px-5 py-2 rounded-t-lg font-semibold border-b-2 transition-colors duration-150 ${
            activeTab === 'past' 
              ? 'border-white text-white bg-black' 
              : 'border-transparent text-gray-400 bg-transparent hover:text-white'
          }`}
          onClick={() => handleTabChange('past')}
        >
          Past
        </button>
      </div>
      
      {/* Bookings Content */}
      <div className="bg-black/20 rounded-xl border border-white/20 overflow-hidden">
        {filteredBookings.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-gray-400 mb-2">
              {activeTab === 'upcoming' ? '📅' : '📚'}
            </div>
            <div className="text-gray-400">
              {activeTab === 'upcoming' 
                ? 'No upcoming bookings scheduled' 
                : 'No past bookings found'
              }
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {activeTab === 'upcoming' 
                ? 'New bookings will appear here' 
                : 'Completed bookings will appear here'
              }
            </div>
          </div>
        ) : (
          <>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="text-white font-medium">
                  {activeTab === 'upcoming' ? 'Upcoming Bookings' : 'Past Bookings'} 
                  <span className="ml-2 text-gray-400">({filteredBookings.length})</span>
                </div>
                {filteredBookings.length > 0 && (
                  <span className="text-sm text-gray-400">
                    {activeTab === 'upcoming' && upcoming.length > 0 && (
                      <>Next pickup: {formatDateShort(upcoming[0]?.datetime)}</>
                    )}
                    {activeTab === 'past' && past.length > 0 && (
                      <>Latest: {formatDateShort(past[past.length - 1]?.datetime)}</>
                    )}
                  </span>
                )}
              </div>
              
              <div className="space-y-4">
                {currentBookings.map(renderBooking)}
              </div>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-white/10">
                <div className="flex justify-center">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded ${
                        currentPage === 1 
                          ? 'bg-gray-800 text-gray-500' 
                          : 'bg-black text-white hover:bg-white/10'
                      }`}
                    >
                      Prev
                    </button>
                    
                    {/* Page numbers */}
                    {[...Array(totalPages)].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`px-3 py-1 rounded ${
                          currentPage === idx + 1 
                            ? 'bg-white text-black' 
                            : 'bg-black text-white hover:bg-white/10'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded ${
                        currentPage === totalPages 
                          ? 'bg-gray-800 text-gray-500' 
                          : 'bg-black text-white hover:bg-white/10'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FleetBookingsOrganized;
